import { Component, Input } from '@angular/core';

/** Dot variant carrying the only color on the pill (REDESIGN.md §0.4). */
export type PdfAiParserStatusDot = 'setup' | 'warning' | 'success' | 'danger' | 'hollow' | 'unknown' | 'parsing';

const STATUS_LABEL_KEYS: { [status: string]: string } = {
  AWAITING_CONFIGURATION: 'AI_PARSER_STATUS_NEEDS_SETUP',
  AWAITING_REVIEW: 'AI_PARSER_STATUS_READY_FOR_REVIEW',
  PARSING: 'AI_PARSER_STATUS_PARSING',
  SUBMITTING: 'AI_PARSER_STATUS_SUBMITTING',
  SUBMIT_OUTCOME_UNKNOWN: 'AI_PARSER_STATUS_SUBMIT_OUTCOME_UNKNOWN',
  DONE: 'AI_PARSER_STATUS_DONE',
  REJECTED: 'AI_PARSER_STATUS_REJECTED',
  ERROR: 'AI_PARSER_STATUS_FAILED',
  // Sender-gate decision (AiOrderParserItem.autoParseDecision) — same pill, a different field.
  AUTO_PARSED: 'AI_PARSER_SENDER_AUTH_PILL_AUTO_PARSED',
  MANUAL_UNAUTHENTICATED: 'AI_PARSER_SENDER_AUTH_PILL_UNAUTHENTICATED',
  MANUAL_UNTRUSTED_FORWARDER: 'AI_PARSER_SENDER_AUTH_PILL_UNTRUSTED_FORWARDER',
  MANUAL_SPAM_SUSPECTED: 'AI_PARSER_SENDER_AUTH_PILL_SPAM_SUSPECTED'
};

const STATUS_DOTS: { [status: string]: PdfAiParserStatusDot } = {
  // Needs setup = blue, ready for review = amber (decision 2026-06-11).
  AWAITING_CONFIGURATION: 'setup',
  AWAITING_REVIEW: 'warning',
  PARSING: 'parsing',
  SUBMITTING: 'parsing',
  SUBMIT_OUTCOME_UNKNOWN: 'danger',
  DONE: 'success',
  REJECTED: 'hollow',
  ERROR: 'danger',
  AUTO_PARSED: 'success',
  MANUAL_UNAUTHENTICATED: 'warning',
  MANUAL_UNTRUSTED_FORWARDER: 'warning',
  MANUAL_SPAM_SUSPECTED: 'warning'
};

/**
 * The one status pill for the AI order parser (REDESIGN.md §0.4): quiet hairline
 * pill where only the 6px dot carries color. Used on the inbox rows, review
 * header and confirm modal, and for the review header's sender-auth pill
 * (`AiOrderParserItem.autoParseDecision` — a different field, same pill). Unknown
 * values fall back to a gray dot with the raw string as label.
 */
@Component({
  selector: 'lib-pdf-ai-parser-status-pill',
  standalone: false,
  templateUrl: './pdf-ai-parser-status-pill.component.html',
  styleUrl: './pdf-ai-parser-status-pill.component.scss'
})
export class PdfAiParserStatusPillComponent {
  /** Backend item status (AiOrderParserItemStatus); unknown values render as-is. */
  @Input() status: string | null | undefined;
  /**
   * "Parsing…" variant with a pulsing accent dot, for a surface that knows a parse is running
   * before the item's status says so. An item already in PARSING gets it from its status.
   */
  @Input() parsing = false;

  get dot(): PdfAiParserStatusDot {
    if (this.parsing) {
      return 'parsing';
    }
    return STATUS_DOTS[this.status || ''] || 'unknown';
  }

  /** Translate key for the label, or undefined for unknown statuses (raw fallback). */
  get labelKey(): string | undefined {
    if (this.parsing) {
      return 'AI_PARSER_STATUS_PARSING';
    }
    return STATUS_LABEL_KEYS[this.status || ''];
  }
}
